/**
 * Il testo cercato, in un posto solo.
 *
 * ## Perché serve un piccolo store e non uno `useState`
 *
 * La ricerca dell'applicativo è una: `SpotlightProvider` — con il catalogo delle pagine, le
 * azioni rapide, e la ricerca dal vivo su task, commesse e persone. Il campo nella barra in
 * alto **non è** una seconda ricerca: è il modo di entrare in quella.
 *
 * Il provider però sta sopra il router (`AppProviders`), la barra sta dentro il layout: fra i
 * due non passa uno stato React senza montare un context apposta e attraversare mezzo albero.
 * Tre funzioni e un `Set` di iscritti fanno la stessa cosa e si leggono in venti righe — lo
 * stesso schema del registro delle voci di ricerca già in uso (`useRegisterSearch`).
 *
 * ## Una sola verità sul testo
 *
 * Il valore vive **qui**, non in due copie: il campo della barra lo scrive, il pannello di
 * ricerca lo legge (e lo riscrive quando si digita là dentro). Con due stati paralleli
 * basterebbe una digitazione veloce perché i due divergano, e la ricerca mostrerebbe i
 * risultati di quello che c'era un attimo prima.
 */
import { spotlight } from '@mantine/spotlight';

let text = '';
const subscribers = new Set<() => void>();

/** Il testo corrente. Stabile fra due notifiche: `useSyncExternalStore` lo pretende. */
export function searchText(): string {
  return text;
}

export function writeSearchText(fresh: string): void {
  if (fresh === text) return;
  text = fresh;
  for (const f of subscribers) f();
}

export function subscribeSearchText(f: () => void): () => void {
  subscribers.add(f);
  return () => { subscribers.delete(f); };
}

/**
 * Apre il pannello di ricerca con il testo già dentro.
 *
 * È il gesto della lente in barra: si scrive lì, e i risultati compaiono dove sono sempre
 * comparsi. Duplicare l'elenco dei risultati sotto il campo avrebbe voluto dire mantenere due
 * ricerche che rispondono alla stessa domanda — e prima o poi in modo diverso.
 */
export function openSearch(withText?: string): void {
  if (withText !== undefined) writeSearchText(withText);
  spotlight.open();
}
